/**
 * Performance Monitor
 * Monitors memory usage and provides performance optimization utilities
 * 
 * Requirements: 16.1, 16.2, 16.3
 */

export interface PerformanceMetrics {
  memoryUsageMB: number;
  memoryLimitMB: number;
  memoryPercentage: number;
  isHighMemory: boolean;
}

export class PerformanceMonitor {
  private static instance: PerformanceMonitor | null = null;
  private memoryCheckInterval: number | null = null;
  private memoryWarningCallback: ((metrics: PerformanceMetrics) => void) | null = null;
  
  // Memory thresholds
  private readonly HIGH_MEMORY_THRESHOLD = 0.8; // 80% of limit
  private readonly TARGET_MEMORY_MB = 100; // Target: < 100MB (Requirement 16.1)

  private constructor() {}

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * Get current memory usage
   * Requirement 16.1: Monitor memory usage < 100MB
   */
  getMemoryUsage(): PerformanceMetrics | null {
    // Check if Performance Memory API is available
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      const usedMB = memory.usedJSHeapSize / (1024 * 1024);
      const limitMB = memory.jsHeapSizeLimit / (1024 * 1024);
      const percentage = usedMB / limitMB;

      return {
        memoryUsageMB: usedMB,
        memoryLimitMB: limitMB,
        memoryPercentage: percentage,
        isHighMemory: usedMB > this.TARGET_MEMORY_MB || percentage > this.HIGH_MEMORY_THRESHOLD,
      };
    }

    return null;
  }

  /**
   * Start monitoring memory usage
   */
  startMonitoring(intervalMs: number = 5000): void {
    if (this.memoryCheckInterval !== null) {
      return; // Already monitoring
    }

    this.memoryCheckInterval = window.setInterval(() => {
      const metrics = this.getMemoryUsage();
      
      if (metrics && metrics.isHighMemory && this.memoryWarningCallback) {
        this.memoryWarningCallback(metrics);
      }
    }, intervalMs);
  }

  /**
   * Stop monitoring memory usage
   */
  stopMonitoring(): void {
    if (this.memoryCheckInterval !== null) {
      clearInterval(this.memoryCheckInterval);
      this.memoryCheckInterval = null;
    }
  }

  /**
   * Register callback for memory warnings
   */
  onMemoryWarning(callback: (metrics: PerformanceMetrics) => void): void {
    this.memoryWarningCallback = callback;
  }

  /**
   * Check if device supports hardware acceleration
   * Requirement 16.3: Use hardware acceleration when available
   */
  supportsHardwareAcceleration(): boolean {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      return !!gl;
    } catch (e) {
      return false;
    }
  }

  /**
   * Check if OffscreenCanvas is supported
   * Requirement 16.2: Use OffscreenCanvas for encoding
   */
  supportsOffscreenCanvas(): boolean {
    return typeof OffscreenCanvas !== 'undefined';
  }

  /**
   * Request garbage collection (if available)
   * Note: Only available in some browsers with specific flags
   */
  requestGarbageCollection(): void {
    if ('gc' in window && typeof (window as any).gc === 'function') {
      try {
        (window as any).gc();
      } catch (e) {
        console.warn('Garbage collection not available');
      }
    }
  }

  /**
   * Get performance timing information
   */
  getPerformanceTiming(): {
    loadTime: number;
    domContentLoaded: number;
    firstPaint?: number;
  } | null {
    if (!performance.timing) {
      return null;
    }

    const timing = performance.timing;
    const loadTime = timing.loadEventEnd - timing.navigationStart;
    const domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;

    // Try to get First Paint time
    let firstPaint: number | undefined;
    if (performance.getEntriesByType) {
      const paintEntries = performance.getEntriesByType('paint');
      const firstPaintEntry = paintEntries.find(entry => entry.name === 'first-paint');
      if (firstPaintEntry) {
        firstPaint = firstPaintEntry.startTime;
      }
    }

    return {
      loadTime,
      domContentLoaded,
      firstPaint,
    };
  }

  /**
   * Measure function execution time
   */
  measureExecutionTime<T>(fn: () => T, label: string): T {
    const start = performance.now();
    const result = fn();
    const end = performance.now();
    console.log(`[Performance] ${label}: ${(end - start).toFixed(2)}ms`);
    return result;
  }

  /**
   * Measure async function execution time
   */
  async measureAsyncExecutionTime<T>(
    fn: () => Promise<T>,
    label: string
  ): Promise<T> {
    const start = performance.now();
    const result = await fn();
    const end = performance.now();
    console.log(`[Performance] ${label}: ${(end - start).toFixed(2)}ms`);
    return result;
  }

  /**
   * Log current performance metrics
   */
  logMetrics(): void {
    const memory = this.getMemoryUsage();
    const timing = this.getPerformanceTiming();
    const supportsOffscreen = this.supportsOffscreenCanvas();
    const supportsHardwareAccel = this.supportsHardwareAcceleration();

    console.group('[Performance Metrics]');
    
    if (memory) {
      console.log(`Memory Usage: ${memory.memoryUsageMB.toFixed(2)} MB / ${memory.memoryLimitMB.toFixed(2)} MB (${(memory.memoryPercentage * 100).toFixed(1)}%)`);
      console.log(`High Memory: ${memory.isHighMemory ? 'YES ⚠️' : 'NO ✓'}`);
    } else {
      console.log('Memory API not available');
    }

    if (timing) {
      console.log(`Load Time: ${timing.loadTime}ms`);
      console.log(`DOM Content Loaded: ${timing.domContentLoaded}ms`);
      if (timing.firstPaint) {
        console.log(`First Paint: ${timing.firstPaint.toFixed(2)}ms`);
      }
    }

    console.log(`OffscreenCanvas Support: ${supportsOffscreen ? 'YES ✓' : 'NO'}`);
    console.log(`Hardware Acceleration: ${supportsHardwareAccel ? 'YES ✓' : 'NO'}`);
    
    console.groupEnd();
  }
}

export default PerformanceMonitor;
